import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'
import _ from 'lodash'

import { loadUsers, management, updateCockpitConfig } from '../backend'
import { useMessages } from '../Messages'
import { useAuth } from './Auth'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from "../navigation";
import { useQuery } from '@tanstack/react-query'

export interface UserSettings {
    user: string,
    userId?: number;
    displayName?: string;
    email?: string;
    emailVerified: boolean;
    language?: string;
    cockpitConfig?: string; // JSON: { [cockpitType]: { order: string[], hidden: string[] } }
}

// Per-cockpit panel layout (order + hidden panel keys), persisted in the backend.
export type CockpitEntry = { order: string[], hidden: string[] }
export type CockpitConfigMap = Record<string, CockpitEntry>

const UserSettingsContext = React.createContext({
    userSettings: undefined as (UserSettings | undefined),
    users: {} as Record<string, UserSettings>,
    updateUserSettings: (settings: UserSettings) => Promise.resolve(),
    cockpitConfig: {} as CockpitConfigMap,
    saveCockpitConfig: (_cockpitType: string, _entry: CockpitEntry) => { /* no-op default */ },
})

export function useUserSettings() {
    return useContext(UserSettingsContext)
}

export function useUserSettingsFor(user: string) {
    const {data: userSettings } = useQuery<UserSettings>({
        queryKey: ['userSettings', user], 
        queryFn: () => management({user, type: "settings", operation: "get"}),
    })
    return {userSettings}
}

export function UserSettingsProvider({children}) {
    const {i18n} = useTranslation()
    const {user, team: realm} = useAuth()
    const {setErrorMessage, setInfoMessage} = useMessages()

    const [hasLoaded, setHasLoaded] = useState(false)
    const [userSettings, setUserSettings] = useState(undefined as (UserSettings | undefined))
    const [users, setUsers] = useState({})

    useEffect(() => {
        const lang = (userSettings?.language ?? i18n.resolvedLanguage)
        if (i18n.resolvedLanguage !== lang)
            i18n.changeLanguage(lang)
    }, [userSettings?.language, i18n])
    

    useEffect(() => {
        if (!hasLoaded) {
            setHasLoaded(true)
            management({ user, type: "settings", operation: "get" })
                .then(setUserSettings)
                .then(() => loadUsers().then(us => setUsers(_.keyBy(us, 'user'))))
                .then(() => setHasLoaded(true))
                .catch(err => setErrorMessage(err.message))
        }
    }, [hasLoaded, user, setErrorMessage])

    useEffect(() => {
        if (hasLoaded && userSettings?.user !== undefined) {
            const email = userSettings?.email
            //console.log({userSettings, email})
            if (email === undefined)
                setInfoMessage(<div><Trans i18nKey="no-email-go-to-settings" components={{settings: <Link className='underline' to="/settings/user"/>}} /></div>)
            return

        }
    }, [hasLoaded, userSettings, setInfoMessage])
    
    async function updateUserSettings(settings?: UserSettings) {
        setUserSettings(settings)
        return management({user, type: "settings", operation: "update", ...settings})
            .then(() => setHasLoaded(false))
    }

    // Full config is nested by realm: { [realm]: { [cockpitType]: { order, hidden } } } — so a
    // user's layout is scoped per (user, realm). `cockpitConfig` exposes just the current realm's
    // slice (fallback: {} → registry defaults), so consumers stay realm-agnostic.
    const cockpitConfigByRealm = useMemo<Record<string, CockpitConfigMap>>(() => {
        try { return userSettings?.cockpitConfig ? JSON.parse(userSettings.cockpitConfig) : {} }
        catch { return {} }
    }, [userSettings?.cockpitConfig])

    const cockpitConfig = useMemo<CockpitConfigMap>(
        () => (realm ? cockpitConfigByRealm[realm] : undefined) ?? {},
        [cockpitConfigByRealm, realm],
    )

    // Merge one cockpit's layout into the current realm's slice, update local state immediately,
    // and persist (debounced) so other realms' layouts and other settings are never clobbered.
    const cockpitSaveTimer = useRef<ReturnType<typeof setTimeout>>()
    const saveCockpitConfig = useCallback((cockpitType: string, entry: CockpitEntry) => {
        if (!realm) return
        const nextRealm: CockpitConfigMap = { ...(cockpitConfigByRealm[realm] ?? {}), [cockpitType]: entry }
        const json = JSON.stringify({ ...cockpitConfigByRealm, [realm]: nextRealm })
        setUserSettings(prev => prev ? { ...prev, cockpitConfig: json } : prev)
        if (cockpitSaveTimer.current) clearTimeout(cockpitSaveTimer.current)
        cockpitSaveTimer.current = setTimeout(() => {
            updateCockpitConfig(user, json).catch(err => setErrorMessage(err.message))
        }, 500)
    }, [cockpitConfigByRealm, realm, user, setErrorMessage])

    return (
        <UserSettingsContext.Provider value={{userSettings, updateUserSettings, users, cockpitConfig, saveCockpitConfig}}>
            {children}
        </UserSettingsContext.Provider>
    )
}